import React, {useEffect, useState} from 'react';
import {DataTable} from "primereact/datatable";
import {Column} from "primereact/column";
import {Message} from "primereact/message";
import {Tag} from "primereact/tag";

import {AppContext} from "../../HomePage";
import {SearchDiagnosticsService} from "../../service/SearchDiagnosticsService";
import {isAdmin} from "../../utils/Utils";
import {SITE_STATUS_SEVERITY} from "../Product/ProductSiteSearchDiagnostics";

const service = new SearchDiagnosticsService();

// Admin-only: one row per site, from that site's LATEST run. Every site is its own job on
// its own schedule, so there is no single "latest campaign run" here. Products skipped
// before a request (already a competitor, missing field, budget) exist only as these
// run-level counts - they get no per-product record on purpose.
const CampaignSiteSearchSummary = ({campaignId, reloadToken}) => {

    const {t, setError} = React.useContext(AppContext);
    const [rows, setRows] = useState(null);
    const [expandedRows, setExpandedRows] = useState(null);

    useEffect(() => {
        if (!isAdmin() || !campaignId) return;
        service.getCampaignSiteSearchSummary(campaignId)
            .then(setRows)
            .catch(error => { setError(error); setError(null); });
    }, [campaignId, reloadToken]); // eslint-disable-line react-hooks/exhaustive-deps

    if (!isAdmin() || !rows || rows.length === 0) return null;

    const label = (key, options) => t(`search_diagnostics.${key}`, options);
    const emptyMessage = t('actions.empty_message');
    const secondary = {color: 'var(--text-color-secondary)'};
    const latest = rows.map(row => row.started_at).filter(Boolean).sort().pop();

    const productLink = (productId, field) => <span key={productId + (field || '')}
                                                   style={{marginRight: '.6em', display: 'inline-block'}}>
        <a href={'/#/productdetail?id=' + productId} target="_blank"
           rel="noopener noreferrer">{productId}</a>
        {field && <span style={secondary}> ({field})</span>}
    </span>;
    // A missing_search_field sample is {product_id, field}; every other sample is an id.
    const sampleLink = (sample) => typeof sample === 'string'
        ? productLink(sample) : productLink(sample.product_id, sample.field);

    const skipEntries = (row) => Object.entries(row.skipped || {})
        .map(([reason, entry]) => ({reason, count: entry.count || 0, samples: entry.samples || []}))
        .sort((a, b) => b.count - a.count);
    const skippedBody = (row) => {
        const entries = skipEntries(row);
        const total = entries.reduce((sum, entry) => sum + entry.count, 0);
        if (!total) return 0;
        return <>{total} <span style={secondary}>· {label(`skip_${entries[0].reason}`)}</span></>;
    };

    const expansion = (row) => {
        const problems = ['blocked', 'errored', 'zero_results'].flatMap(status =>
            ((row.sample_problems || {})[status] || []).map(problem => ({...problem, status})));
        return <div style={{padding: '.5em 1em'}}>
            <h6>{label('site_skipped')}</h6>
            <DataTable value={skipEntries(row)} className="p-datatable-sm" emptyMessage={emptyMessage}>
                <Column header={label('site_skip_reason')} body={(entry) => label(`skip_${entry.reason}`)}/>
                <Column field="count" header={label('products_column')} style={{width: '7rem'}}/>
                <Column header={label('site_skip_samples')} body={(entry) => entry.samples.map(sampleLink)}/>
            </DataTable>
            <h6 style={{marginTop: '1em'}}>{label('site_problems')}</h6>
            <DataTable value={problems} className="p-datatable-sm" emptyMessage={emptyMessage}>
                <Column header={label('site_status')}
                        body={(problem) => <Tag severity={SITE_STATUS_SEVERITY[problem.status] || null}
                                                value={label(`site_status_${problem.status}`)}/>}/>
                <Column header={label('site_product')} body={(problem) => productLink(problem.product_id)}/>
                <Column header={label('site_keyword')} body={(problem) => <code>{problem.query || '-'}</code>}/>
                <Column field="http_status" header={label('http')} style={{width: '6rem'}}/>
                <Column header={label('engine_url')} style={{width: '5rem'}}
                        body={(problem) => problem.url
                            ? <a href={problem.url} target="_blank" rel="noreferrer">{label('open')}</a> : '-'}/>
                <Column header={label('reason')} body={(problem) => problem.detail || ''}/>
            </DataTable>
        </div>;
    };

    return <div style={{marginTop: '1.5em'}}>
        <h6>{label('site_title')}
            <span style={{fontWeight: 'normal', marginLeft: '.6em', fontSize: '.85em', ...secondary}}>
                {rows.length} {label('site_hosts')}
                {latest && <> · {new Date(latest).toLocaleString()}</>}
            </span>
        </h6>

        {/* Every searched product came back empty: a 200-status block page or a broken
            parser. Neither is detectable per page, so this is the site-level signal. */}
        {rows.filter(row => row.all_zero_results).map(row =>
            <Message key={row.host} severity="warn" style={{marginBottom: '.5em', display: 'block'}}
                     text={label('site_all_zero_alarm',
                                 {host: row.host, count: row.totals?.products_searched || 0})}/>)}

        <DataTable value={rows} dataKey="host" expandedRows={expandedRows}
                   onRowToggle={(e) => setExpandedRows(e.data)} rowExpansionTemplate={expansion}
                   responsiveLayout="scroll" className="p-datatable-sm">
            <Column expander style={{width: '3em'}}/>
            <Column header={label('host')} body={(row) => <>
                {row.host}
                {row.all_zero_results &&
                    <Tag severity="danger" value={label('site_alarm')} style={{marginLeft: '.4em'}}/>}
                {row.target === 'supplier' &&
                    <Tag severity="info" value={label('site_supplier')} style={{marginLeft: '.4em'}}/>}
            </>}/>
            <Column header={label('site_last_search')} body={(row) => <>
                {row.started_at && new Date(row.started_at).toLocaleString()} · {label(`trigger_${row.trigger}`)}
            </>}/>
            <Column header={label('site_searched')} body={(row) => row.totals?.products_searched || 0}/>
            <Column header={label('suggested')} body={(row) => <>
                {row.totals?.suggested || 0} <span style={secondary}>({row.suggestions_per_searched_product})</span>
            </>}/>
            <Column header={label('site_status_zero_results')} body={(row) => row.totals?.zero_results || 0}/>
            <Column header={label('site_status_blocked')} body={(row) => row.totals?.blocked || 0}/>
            <Column header={label('site_status_errored')} body={(row) => row.totals?.errored || 0}/>
            <Column header={label('site_skipped')} body={skippedBody}/>
            <Column header={label('site_budget')}
                    body={(row) => row.budget ? `${row.budget.used} / ${row.budget.limit}` : '-'}/>
        </DataTable>
    </div>;
};

export default CampaignSiteSearchSummary;
